/**
 * A place's schedule (place manager, unit 6), read-only: what is coming up
 * here (Next up), what repeats here (Repeats) and the templates tied to it
 * (Templates). The reads come in through {@link ScheduleApi} and
 * {@link StaffScheduleApi}, so the paging, the visibility rule and who belongs
 * where are unit-testable without a network; the real reads live in
 * `place-schedule-api.ts`.
 *
 * Next up reads the place's seasons that have not ended, the same page read
 * how the last ones went uses (`readSeason` in `last-events.ts`), here
 * stopping once a page reaches events that have started. Who sees what is
 * that section's rule too: for a partner, an event whose details read null is
 * private to them and is dropped; for staff it is gone, and its row stays,
 * said as not read.
 *
 * Repeats and templates are admin-only reads, made only for staff and only
 * when the section is opened ({@link readStaffSchedule}); a partner never
 * makes them, and their groups say they are shown to pop.in staff for now.
 * Neither has a place of its own. Who belongs here is {@link tieOf}: the
 * template's JSON names this place, or else one of this place's seasons.
 */
import { clockLabel } from "@/lib/dates";
import { plural } from "@/lib/utils";
import type { PlaceSeasonsData, SeasonEventPage } from "@/data/place-events-api";
import type { EventTemplateData, ScheduledEventData, UpcomingDetail } from "@/data/place-schedule-api";
import {
  MAX_PAGES,
  SEASONS_READ,
  readSeason,
  wasCancelled,
  type EventsViewer,
  type SeasonEvent,
  type SeasonRead,
} from "@/features/places/last-events";

/** How many events Next up shows. */
export const NEXT_UP_SHOWN = 10;
/** Templates the templates read asks for (`eventTemplates(first:)`). The
 *  resolver ignores it (it sends every template, up to 9,999), so it never
 *  cuts the list; it only sets the read's cost. */
export const TEMPLATES_ASKED = 100;

type Season = PlaceSeasonsData["seasons"]["nodes"][number];
type ListedEvent = SeasonEventPage["nodes"][number];

/** The reads Next up makes (`place-schedule-api.ts`, with the viewer bound). */
export interface ScheduleApi {
  seasonEvents(seasonId: string, offset: number): Promise<SeasonEventPage | null>;
  /** The game and rules preset of the events shown, in one read, in the order
   *  asked (null: not visible to this viewer, or gone; `unread`: found, its
   *  details failed). */
  details(eventIds: readonly string[]): Promise<UpcomingDetail[]>;
}

/** The two staff-only reads (`place-schedule-api.ts`, through the staff door). */
export interface StaffScheduleApi {
  templates(): Promise<EventTemplateData[]>;
  repeats(): Promise<ScheduledEventData[]>;
}

/** An event still to come: it starts after `now` and was not cancelled. */
export function isUpcoming(event: ListedEvent, now: number): boolean {
  return event.startAt > now && !wasCancelled(event);
}

/** Whether a season's events so far (newest first) call for the next page:
 *  the oldest one read is still to come, so sooner ones may follow. */
export function stillToCome(events: readonly ListedEvent[], now: number): boolean {
  const oldest = events[events.length - 1];
  return oldest !== undefined && oldest.startAt > now;
}

/** The seasons read for Next up: those not yet ended, soonest start first (so
 *  the seasons running now, then the next to begin), at most
 *  {@link SEASONS_READ}. A season that has ended has nothing still to come. */
export function seasonsForUpcoming(seasons: readonly Season[], now: number): Season[] {
  return seasons
    .filter((s) => s.endAt > now)
    .sort((a, b) => a.startAt - b.startAt)
    .slice(0, SEASONS_READ);
}

/** The first `n` events still to come across the seasons read, soonest first.
 *  Equal start times keep the seasons' own order. */
export function soonestUpcoming(reads: readonly SeasonRead[], now: number, n: number = NEXT_UP_SHOWN): SeasonEvent[] {
  return reads
    .flatMap((r) => r.events.filter((e) => isUpcoming(e, now)).map((e) => ({ ...e, season: r.season.title })))
    .sort((a, b) => a.startAt - b.startAt)
    .slice(0, n);
}

/** One Next up row. */
export interface NextUpRow {
  id: string;
  title: string;
  startAt: number;
  season: string;
  /** The game, by name; null when the details did not read. */
  game: string | null;
  /** The rules preset, by name; null when the details did not read. */
  preset: string | null;
  /** True when the details did not read (the read failed; or, to staff, the
   *  event is gone). A partner never gets a row for an event not visible to them. */
  unread: boolean;
}

/** What Next up draws for one place. */
export interface NextUp {
  /** The next {@link NEXT_UP_SHOWN} events still to come, soonest first. */
  rows: NextUpRow[];
  /** Events dropped from the rows as not visible to this viewer: private
   *  events, to a partner. Always 0 for staff. */
  notShown: number;
  /** Events whose lookup failed (the whole details read, or that one event's),
   *  so whether this viewer may see them is not known: a partner gets no row
   *  for them; staff keep the row, said as not read. */
  failed: number;
  /** Every row asked for was filled (before any were dropped), so more may
   *  be coming up than show. */
  full: boolean;
  /** Seasons read; all the place's seasons this viewer sees; `cut` when some
   *  seasons not ended were not read (only the {@link SEASONS_READ} starting
   *  soonest are), or the place has more seasons than were listed; `capped`
   *  when a season's read stopped at {@link MAX_PAGES} with every event read
   *  still to come, so sooner ones in it were not read. */
  seasons: { read: number; total: number; cut: boolean; capped: boolean };
}

/** One shown event's details as read (see `UpcomingDetail`). When the whole
 *  details read failed, every event is `failed`. */
export type ShownDetails = UpcomingDetail;

/** Next up's model from what was read: rows for `shown` (picked by
 *  {@link soonestUpcoming}), each with its details from `detailsOf`. For a
 *  partner, an event whose details read null is not visible to them and is
 *  dropped; for staff it is gone, and its row says its details did not read.
 *  An event found whose details failed (`unread`) keeps its row, said as not
 *  read, for anyone. An event whose lookup failed (`failed`, one or all) gets
 *  no row for a partner (it could be private: unlike how the last ones went,
 *  on purpose) and is counted as not read; staff keep the row, said as not read. */
export function nextUpOf(
  seasons: PlaceSeasonsData,
  reads: readonly SeasonRead[],
  shown: readonly SeasonEvent[],
  detailsOf: (eventId: string) => ShownDetails,
  now: number,
  viewer: EventsViewer,
): NextUp {
  const rows: NextUpRow[] = [];
  let notShown = 0;
  let failed = 0;
  for (const e of shown) {
    const details = detailsOf(e.id);
    if (details === "failed" && !viewer.isSuperAdmin) {
      failed++;
      continue;
    }
    if (details === null && !viewer.isSuperAdmin) {
      notShown++;
      continue;
    }
    const read = details === "failed" || details === "unread" ? null : details;
    rows.push({
      id: e.id,
      title: e.title,
      startAt: e.startAt,
      season: e.season,
      game: read?.gatheringActivity.name ?? null,
      preset: read?.gatheringSettings.name ?? null,
      unread: read === null,
    });
  }
  const notEnded = seasons.seasons.nodes.filter((s) => s.endAt > now).length;
  return {
    rows,
    notShown,
    failed,
    full: shown.length === NEXT_UP_SHOWN,
    seasons: {
      read: reads.length,
      total: seasons.seasons.totalCount,
      cut: seasons.seasons.totalCount > seasons.seasons.nodes.length || notEnded > reads.length,
      capped: reads.some((r) => r.events.length < r.total && stillToCome(r.events, now)),
    },
  };
}

/** Read what is coming up at a place: from its seasons (already read, shared
 *  with the other sections), the events of those not ended (seasons side by
 *  side, each paging on its own until it reaches events that have started),
 *  then the details of just the events shown, in one read. That read failing
 *  leaves staff every row without its game and preset, and a partner none
 *  (see {@link nextUpOf}); a season read failing fails the whole. */
export async function readNextUp(
  seasons: PlaceSeasonsData,
  api: ScheduleApi,
  now: number,
  viewer: EventsViewer,
): Promise<NextUp> {
  const reads = await Promise.all(
    seasonsForUpcoming(seasons.seasons.nodes, now).map((s) => readSeason(s, api, now, stillToCome)),
  );
  const shown = soonestUpcoming(reads, now);
  let details: Map<string, UpcomingDetail> | null = null;
  if (shown.length > 0) {
    try {
      const read = await api.details(shown.map((e) => e.id));
      details = new Map(shown.map((e, i) => [e.id, read[i] === undefined ? "failed" : read[i]]));
    } catch {
      details = null;
    }
  }
  return nextUpOf(seasons, reads, shown, (id) => (details ? (details.get(id) ?? null) : "failed"), now, viewer);
}

/** The keys of a template's JSON the schedule reads; null where absent (or
 *  not of the kind the backend reads: a key is never guessed). */
export interface TemplateKeys {
  /** The place the events are made at (`placeId`, a place's public id). */
  placeId: string | null;
  /** The season the events are made in (`seasonId`, a season's public id). */
  seasonId: string | null;
  /** The game, by slug (`gatheringActivitySlug`). */
  game: string | null;
  /** The tables' rules preset, by slug (`gatheringSettingsTagSlug`). */
  preset: string | null;
  /** The lobby's rules preset, by slug (`lobbyGatheringSettingsTagSlug`). */
  lobbyPreset: string | null;
}

function text(json: Record<string, unknown>, key: string): string | null {
  const v = json[key];
  return typeof v === "string" && v.trim() !== "" ? v : null;
}


/** A template's JSON (`EventTemplate.templateJson`) as an object; null when it
 *  does not read as one. */
export function templateObjectOf(templateJson: string): Record<string, unknown> | null {
  let json: unknown;
  try {
    json = JSON.parse(templateJson);
  } catch {
    return null;
  }
  return json !== null && typeof json === "object" && !Array.isArray(json) ? (json as Record<string, unknown>) : null;
}

/** The keys of a template's JSON (`EventTemplate.templateJson`); null when it
 *  is not a JSON object. */
export function templateKeysOf(templateJson: string): TemplateKeys | null {
  const o = templateObjectOf(templateJson);
  if (!o) return null;
  return {
    placeId: text(o, "placeId"),
    seasonId: text(o, "seasonId"),
    game: text(o, "gatheringActivitySlug"),
    preset: text(o, "gatheringSettingsTagSlug"),
    lobbyPreset: text(o, "lobbyGatheringSettingsTagSlug"),
  };
}

/** Where a template (or a repeat, through its template) belongs, as this
 *  place sees it: `here`; `elsewhere` (it names another place, or a season
 *  that is not one of this place's); `untied` (it names neither a place nor a
 *  season); `unread` (its JSON did not read). */
export type Tie = "here" | "elsewhere" | "untied" | "unread";

/** The attribution rule: a template belongs to this place when its JSON's
 *  `placeId` is this place; else, with no `placeId`, when its `seasonId` is
 *  one of this place's seasons (an event with no place of its own takes its
 *  season's). A `placeId` naming another place wins over a season of this
 *  place's: the backend makes the event at that place. */
export function tieOf(keys: TemplateKeys | null, placeId: string, seasonIds: ReadonlySet<string>): Tie {
  if (!keys) return "unread";
  if (keys.placeId) return keys.placeId === placeId ? "here" : "elsewhere";
  if (keys.seasonId) return seasonIds.has(keys.seasonId) ? "here" : "elsewhere";
  return "untied";
}

/** What `EventTemplate.defaultDuration` reads when none was set (the
 *  backend's fallback, `types/eventTemplate.ts`): 45 minutes. Events made from
 *  such a template in the admin GUI do run that long, so it is shown, said as
 *  the default. */
export const DEFAULT_DURATION_MS = 45 * 60 * 1000;

/** One template tied to this place. Slugs are shown as they are (no catalog
 *  read here); null where the JSON does not say. `durationMs` is the
 *  template's own `defaultDuration`: the admin GUI's "new event from template"
 *  sets the event's times from it (the JSON's saved start and wind-down times
 *  are replaced), so it is how long those events run. Repeats run their own
 *  `eventDurationInMs` instead. */
export interface TemplateRow {
  id: string;
  name: string;
  game: string | null;
  preset: string | null;
  lobbyPreset: string | null;
  durationMs: number;
  /** The template's whole JSON, as read: scheduling an event from it starts
   *  from all of it (`schedule-event.ts`). */
  templateJson: string;
}

/** One repeat tied to this place: its days and time are in its own time zone. */
export interface RepeatRow {
  id: string;
  name: string;
  /** The days it makes an event on, as the backend names them ("Mon" … "Sun"). */
  days: string[];
  hour: number;
  minute: number;
  timeZone: string;
  /** How long each event runs (the backend sets each event's end from it). */
  durationMs: number;
  game: string | null;
  preset: string | null;
  /** Its template's name. */
  template: string;
  /** When the backend makes its next event (ms since epoch). */
  nextMadeAt: number;
}

/** A staff-only group: `staff-only` for anyone but staff (never read);
 *  `unread` until staff open the section; then `reading`, `failed`, or the
 *  rows tied to this place, with how many are tied to no place (`untied`) and
 *  how many could not be told (`unread`, their JSON did not read). Both reads
 *  come back whole (the templates resolver ignores `first:`; the repeats read
 *  takes no arguments), so neither says "there may be more". */
export type StaffGroup<Row> =
  | { state: "staff-only" | "unread" | "reading" | "failed" }
  | { state: "ready"; rows: Row[]; untied: number; unread: number };

/** The two staff reads as they came back (each on its own: one failing leaves
 *  the other). They list every template and repeat, not one place's. */
export interface StaffRead {
  templates: EventTemplateData[] | "failed";
  repeats: ScheduledEventData[] | "failed";
}

/** Read the templates and the repeats, side by side, for staff only. For
 *  anyone else nothing is asked for at all, and the answer is null. */
export async function readStaffSchedule(viewer: EventsViewer, api: StaffScheduleApi): Promise<StaffRead | null> {
  if (!viewer.isSuperAdmin) return null;
  const [templates, repeats] = await Promise.all([
    api.templates().catch((): "failed" => "failed"),
    api.repeats().catch((): "failed" => "failed"),
  ]);
  return { templates, repeats };
}

function grouped<T, Row>(
  items: readonly T[],
  json: (item: T) => string,
  row: (item: T, keys: TemplateKeys) => Row,
  placeId: string,
  seasonIds: ReadonlySet<string>,
): StaffGroup<Row> {
  const rows: Row[] = [];
  let untied = 0;
  let unread = 0;
  for (const item of items) {
    const keys = templateKeysOf(json(item));
    const tie = tieOf(keys, placeId, seasonIds);
    if (tie === "here") rows.push(row(item, keys!));
    else if (tie === "untied") untied++;
    else if (tie === "unread") unread++;
  }
  return { state: "ready", rows, untied, unread };
}

/** The templates tied to this place (in the backend's sort order). */
export function templatesOf(
  templates: readonly EventTemplateData[],
  placeId: string,
  seasonIds: ReadonlySet<string>,
): StaffGroup<TemplateRow> {
  return grouped(
    templates,
    (t) => t.templateJson,
    (t, k) => ({
      id: t.id,
      name: t.name,
      game: k.game,
      preset: k.preset,
      lobbyPreset: k.lobbyPreset,
      durationMs: t.defaultDuration,
      templateJson: t.templateJson,
    }),
    placeId,
    seasonIds,
  );
}

/** The repeats tied to this place, through their templates (by name, as the backend sends them). */
export function repeatsOf(
  repeats: readonly ScheduledEventData[],
  placeId: string,
  seasonIds: ReadonlySet<string>,
): StaffGroup<RepeatRow> {
  return grouped(
    repeats,
    (r) => r.eventTemplate.templateJson,
    (r, k) => ({
      id: r.id,
      name: r.name,
      days: r.startEventOnDays,
      hour: r.eventStartTimeHour,
      minute: r.eventStartTimeMinute,
      timeZone: r.timeZone,
      durationMs: r.eventDurationInMs,
      game: k.game,
      preset: k.preset,
      template: r.eventTemplate.name,
      nextMadeAt: r.nextEventToBeCreatedAt,
    }),
    placeId,
    seasonIds,
  );
}

/** What the Schedule section draws for one place. */
export interface PlaceSchedule {
  nextUp: NextUp;
  repeats: StaffGroup<RepeatRow>;
  templates: StaffGroup<TemplateRow>;
  /** The place has more seasons than were listed, so a template tied by one
   *  of its older seasons is not found here. */
  seasonsCut: boolean;
}

/** Where the staff reads are: not asked for yet, asked for and not back, or back. */
export type StaffState = "unread" | "reading" | StaffRead;

/** The section's model: Next up as read, and the staff groups from `staff`
 *  (always `staff-only` for anyone but staff, whatever `staff` holds). */
export function placeScheduleOf(
  placeId: string,
  seasons: PlaceSeasonsData,
  nextUp: NextUp,
  staff: StaffState,
  viewer: EventsViewer,
): PlaceSchedule {
  const seasonsCut = seasons.seasons.totalCount > seasons.seasons.nodes.length;
  if (!viewer.isSuperAdmin) {
    return { nextUp, repeats: { state: "staff-only" }, templates: { state: "staff-only" }, seasonsCut };
  }
  if (staff === "unread" || staff === "reading") {
    return { nextUp, repeats: { state: staff }, templates: { state: staff }, seasonsCut };
  }
  const seasonIds = new Set(seasons.seasons.nodes.map((s) => s.id));
  return {
    nextUp,
    repeats: staff.repeats === "failed" ? { state: "failed" } : repeatsOf(staff.repeats, placeId, seasonIds),
    templates: staff.templates === "failed" ? { state: "failed" } : templatesOf(staff.templates, placeId, seasonIds),
    seasonsCut,
  };
}

/** The days of the week as the backend names them, Monday first (its weeks do). */
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A repeat's days, in week order: "Tue, Thu"; "every day" for all seven. A
 *  day the backend names some other way is shown as it is, last. */
export function daysLabel(days: readonly string[]): string {
  const known = WEEK.filter((d) => days.includes(d));
  if (known.length === WEEK.length) return "every day";
  const other = days.filter((d) => !WEEK.includes(d));
  const all = [...known, ...other];
  return all.length > 0 ? all.join(", ") : "no days set";
}

/** When a repeat makes its events, in its own time zone, named: "Tue, Thu ·
 *  7:00 PM America/Los_Angeles". */
export function repeatWhenLabel(row: Pick<RepeatRow, "days" | "hour" | "minute" | "timeZone">, locale?: string): string {
  return `${daysLabel(row.days)} · ${clockLabel(row.hour, row.minute, locale)} ${row.timeZone}`;
}

/** A duration in hours and minutes: "1 h 30 min", "45 min", "2 h". */
export function durationLabel(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The quiet line under the staff groups: how many templates and repeats are
 *  tied to no place at all (counted, never listed); null when none are. */
export function untiedLine(schedule: Pick<PlaceSchedule, "templates" | "repeats">): string | null {
  const t = schedule.templates.state === "ready" ? schedule.templates.untied : 0;
  const r = schedule.repeats.state === "ready" ? schedule.repeats.untied : 0;
  if (t + r === 0) return null;
  const parts = [t > 0 && `${t} ${plural(t, "template")}`, r > 0 && `${r} ${plural(r, "repeat")}`].filter(Boolean);
  return `${parts.join(" and ")} ${t + r === 1 ? "is" : "are"} not tied to any place.`;
}
